/**
 * RQC — Requisição de Compras do escritório (ADR-0074).
 *
 * Página única (não é o wizard das assistências): quem pede papel e toner
 * está no computador e costuma pedir vários itens de uma vez. O envio cai no
 * quadro do comprador em "A cotar"; se o comprador ainda não mexeu numa
 * requisição RQC aberta, o backend soma ali — a tela diz onde caiu.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft, FiPaperclip, FiPlus, FiTrash2, FiX } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { buscarProdutosRqc, enviarRqc, opcoesRqc } from "../../../services/rqc";
import { PageContainer, PageTitle, PageTopBar } from "../../../styles/shared";
import { formatarQuantidade, parseDinheiro } from "../../../utils/dinheiro";
import ComboboxBusca from "./ComboboxBusca";
import {
  AcaoLeveBtn, AddBtn, BtnSecundario, Card, ErroBox, Field, Hint, InlineError,
  Input, Label, Linha2, SegmentoBotoes, SegmentoBtn, Select,
} from "./styles";
import { gerarUid } from "./utils";

const EXTENSOES_ACEITAS = ".pdf,.jpg,.jpeg,.png,.heic,.dwg";
const CASAS_QTD = 3;

function hojeLocalISO() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function novoItem() {
  return {
    uid: gerarUid(), cadastrado: true, produto: null, descricao: "", unidade: "UN",
    categoria: "", quantidade: "", marca: "", modelo: "", especificacao: "", observacao: "",
  };
}

function quantidadeValida(texto) {
  const n = parseDinheiro(texto);
  return n !== null && n > 0 && Number(n.toFixed(CASAS_QTD)) === n ? n : null;
}

const TextArea = (props) => <Input as="textarea" rows={3} style={{ height: "auto", minHeight: 76, paddingTop: 10 }} {...props} />;

/** Busca no catálogo pelo servidor; o ComboboxBusca filtra o que chegou. */
function ProdutoCampo({ item, onSelecionar, inputId }) {
  const { request } = useAuth();
  const [opcoes, setOpcoes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const temporizador = useRef(null);

  function buscar(texto) {
    clearTimeout(temporizador.current);
    temporizador.current = setTimeout(async () => {
      setCarregando(true);
      try {
        const lista = await buscarProdutosRqc(request, texto.trim());
        setOpcoes((lista || []).map((p) => ({
          ...p, label: p.nome, sublabel: `${p.codigo} · ${p.unidade} · ${p.categoria}`,
        })));
      } catch {
        setOpcoes([]);
      } finally {
        setCarregando(false);
      }
    }, 250);
  }

  useEffect(() => {
    buscar("");
    return () => clearTimeout(temporizador.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ComboboxBusca
      inputId={inputId}
      opcoes={opcoes}
      valor={item.produto}
      onSelecionar={onSelecionar}
      onTextoChange={buscar}
      carregando={carregando}
      placeholder="Buscar produto por nome ou código"
    />
  );
}

export default function CriarRqc() {
  const navigate = useNavigate();
  const { request } = useAuth();
  const toast = useToast();

  const [opcoes, setOpcoes] = useState(null);
  const [cab, setCab] = useState({
    prioridade: "NORMAL", motivo_urgencia: "", data_necessaria: "", motivo: "", justificativa: "",
  });
  const [itens, setItens] = useState([novoItem()]);
  const [anexos, setAnexos] = useState([]);
  const [tentouEnviar, setTentouEnviar] = useState(false);
  const [erroServidor, setErroServidor] = useState("");

  useEffect(() => {
    opcoesRqc(request).then(setOpcoes).catch(() => setOpcoes(null));
  }, [request]);

  const maxAnexos = opcoes?.max_anexos || 5;
  const mudarCab = (campo, valor) => setCab((c) => ({ ...c, [campo]: valor }));
  const mudarItem = (uid, campo, valor) => setItens((lista) => lista.map((it) => (it.uid === uid ? { ...it, [campo]: valor } : it)));

  const erros = useMemo(() => {
    const e = {};
    if (cab.prioridade === "URGENTE" && !cab.motivo_urgencia.trim()) e.motivo_urgencia = "Explique o motivo da urgência.";
    if (!cab.data_necessaria) e.data_necessaria = "Informe a data.";
    else if (cab.data_necessaria < hojeLocalISO()) e.data_necessaria = "A data não pode estar no passado.";
    if (!cab.motivo) e.motivo = "Escolha o motivo.";
    if (!cab.justificativa.trim()) e.justificativa = "Explique a necessidade da compra.";
    itens.forEach((it) => {
      if (quantidadeValida(it.quantidade) === null) e[`${it.uid}_quantidade`] = `Maior que zero, até ${CASAS_QTD} casas.`;
      if (it.cadastrado && !it.produto) e[`${it.uid}_produto`] = "Escolha o produto da lista.";
      if (!it.cadastrado && !it.descricao.trim()) e[`${it.uid}_descricao`] = "Descreva o item.";
      if (!it.cadastrado && !it.categoria) e[`${it.uid}_categoria`] = "Escolha a categoria.";
    });
    return e;
  }, [cab, itens]);
  const erro = (campo) => (tentouEnviar ? erros[campo] : "");

  function adicionarAnexos(lista) {
    const novos = Array.from(lista || []);
    if (anexos.length + novos.length > maxAnexos) {
      toast.error(`Anexe no máximo ${maxAnexos} arquivos.`);
    }
    setAnexos((atuais) => [...atuais, ...novos].slice(0, maxAnexos));
  }

  function montarDados() {
    return {
      ...cab,
      motivo_urgencia: cab.prioridade === "URGENTE" ? cab.motivo_urgencia.trim() : "",
      justificativa: cab.justificativa.trim(),
      itens: itens.map((it) => ({
        ...(it.cadastrado
          ? { produto_id: it.produto.id }
          : { descricao: it.descricao.trim(), unidade: it.unidade, categoria: it.categoria }),
        quantidade: String(quantidadeValida(it.quantidade)),
        marca: it.marca.trim(),
        modelo: it.modelo.trim(),
        especificacao: it.especificacao.trim(),
        observacao: it.observacao.trim(),
      })),
    };
  }

  async function enviar() {
    setTentouEnviar(true);
    setErroServidor("");
    if (Object.keys(erros).length) {
      throw new Error("Verifique os campos destacados.");
    }
    try {
      const rqc = await enviarRqc(request, montarDados(), anexos);
      const req = rqc.requisicao;
      toast.success(`RQC enviado — está na requisição ${req?.numero || ""} (${req?.situacao_label || "A cotar"}).`);
      navigate(`../rqc/${rqc.id}`, { replace: true });
    } catch (e) {
      setErroServidor(e?.payload?.detalhe || e?.message || "Não foi possível enviar o RQC.");
      throw e;
    }
  }

  return (
    <PageContainer style={{ maxWidth: 860 }}>
      <PageTopBar>
        <button type="button" className="btn btn--secondary btn--sm" onClick={() => navigate("..")} aria-label="Voltar">
          <FiArrowLeft size={14} />
        </button>
        <PageTitle>Requisição de Compras (RQC)</PageTitle>
      </PageTopBar>

      <div style={{ display: "grid", gap: 16 }}>
        <Card>
          <Field>
            <Label as="div">Prioridade</Label>
            <SegmentoBotoes $cols={2}>
              {(opcoes?.prioridades || [{ valor: "NORMAL", rotulo: "Normal" }, { valor: "URGENTE", rotulo: "Urgente" }]).map((o) => (
                <SegmentoBtn key={o.valor} type="button" $ativo={cab.prioridade === o.valor} aria-pressed={cab.prioridade === o.valor} onClick={() => mudarCab("prioridade", o.valor)}>
                  {o.rotulo}
                </SegmentoBtn>
              ))}
            </SegmentoBotoes>
          </Field>
          {cab.prioridade === "URGENTE" ? (
            <Field>
              <Label htmlFor="rqc-urgencia">Motivo da urgência</Label>
              <Input id="rqc-urgencia" value={cab.motivo_urgencia} maxLength={300} onChange={(e) => mudarCab("motivo_urgencia", e.target.value)} />
              {erro("motivo_urgencia") ? <InlineError>{erro("motivo_urgencia")}</InlineError> : null}
            </Field>
          ) : null}
          <Linha2>
            <Field>
              <Label htmlFor="rqc-data">Data necessária</Label>
              <Input id="rqc-data" type="date" min={hojeLocalISO()} value={cab.data_necessaria} onChange={(e) => mudarCab("data_necessaria", e.target.value)} />
              {erro("data_necessaria") ? <InlineError>{erro("data_necessaria")}</InlineError> : null}
            </Field>
            <Field>
              <Label htmlFor="rqc-motivo">Motivo da solicitação</Label>
              <Select id="rqc-motivo" value={cab.motivo} onChange={(e) => mudarCab("motivo", e.target.value)}>
                <option value="">Escolha…</option>
                {(opcoes?.motivos || []).map((o) => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
              </Select>
              {erro("motivo") ? <InlineError>{erro("motivo")}</InlineError> : null}
            </Field>
          </Linha2>
          <Field>
            <Label htmlFor="rqc-justificativa">Justificativa</Label>
            <TextArea id="rqc-justificativa" placeholder="Explique a necessidade da compra." value={cab.justificativa} onChange={(e) => mudarCab("justificativa", e.target.value)} />
            {erro("justificativa") ? <InlineError>{erro("justificativa")}</InlineError> : null}
          </Field>
        </Card>

        {itens.map((it, indice) => {
          const qtd = quantidadeValida(it.quantidade);
          return (
            <Card key={it.uid}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <strong style={{ fontSize: 14 }}>Item {indice + 1}</strong>
                <span style={{ flex: 1 }} />
                {itens.length > 1 ? (
                  <AcaoLeveBtn type="button" $danger onClick={() => setItens((l) => l.filter((x) => x.uid !== it.uid))}>
                    <FiTrash2 size={14} /> Remover
                  </AcaoLeveBtn>
                ) : null}
              </div>
              <SegmentoBotoes $cols={2}>
                <SegmentoBtn type="button" $ativo={it.cadastrado} aria-pressed={it.cadastrado} onClick={() => mudarItem(it.uid, "cadastrado", true)}>Produto cadastrado</SegmentoBtn>
                <SegmentoBtn type="button" $ativo={!it.cadastrado} aria-pressed={!it.cadastrado} onClick={() => mudarItem(it.uid, "cadastrado", false)}>Não cadastrado</SegmentoBtn>
              </SegmentoBotoes>

              {it.cadastrado ? (
                <Field>
                  <Label htmlFor={`rqc-prod-${it.uid}`}>Produto</Label>
                  <ProdutoCampo item={it} inputId={`rqc-prod-${it.uid}`} onSelecionar={(p) => mudarItem(it.uid, "produto", p)} />
                  {it.produto ? <Hint>{it.produto.sublabel}</Hint> : null}
                  {erro(`${it.uid}_produto`) ? <InlineError>{erro(`${it.uid}_produto`)}</InlineError> : null}
                </Field>
              ) : (
                <>
                  <Field>
                    <Label htmlFor={`rqc-desc-${it.uid}`}>Descrição do item</Label>
                    <Input id={`rqc-desc-${it.uid}`} maxLength={300} value={it.descricao} onChange={(e) => mudarItem(it.uid, "descricao", e.target.value)} />
                    {erro(`${it.uid}_descricao`) ? <InlineError>{erro(`${it.uid}_descricao`)}</InlineError> : null}
                  </Field>
                  <Linha2>
                    <Field>
                      <Label htmlFor={`rqc-un-${it.uid}`}>Unidade</Label>
                      <Select id={`rqc-un-${it.uid}`} value={it.unidade} onChange={(e) => mudarItem(it.uid, "unidade", e.target.value)}>
                        {(opcoes?.unidades || [{ valor: "UN", rotulo: "Unidade" }]).map((o) => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
                      </Select>
                    </Field>
                    <Field>
                      <Label htmlFor={`rqc-cat-${it.uid}`}>Categoria</Label>
                      <Select id={`rqc-cat-${it.uid}`} value={it.categoria} onChange={(e) => mudarItem(it.uid, "categoria", e.target.value)}>
                        <option value="">Escolha…</option>
                        {(opcoes?.categorias || []).map((o) => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
                      </Select>
                      {erro(`${it.uid}_categoria`) ? <InlineError>{erro(`${it.uid}_categoria`)}</InlineError> : null}
                    </Field>
                  </Linha2>
                </>
              )}

              <Field>
                <Label htmlFor={`rqc-qtd-${it.uid}`}>Quantidade</Label>
                <Input
                  id={`rqc-qtd-${it.uid}`}
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={it.quantidade}
                  onChange={(e) => mudarItem(it.uid, "quantidade", e.target.value)}
                  aria-invalid={Boolean(erro(`${it.uid}_quantidade`))}
                />
                {erro(`${it.uid}_quantidade`) ? (
                  <InlineError>{erro(`${it.uid}_quantidade`)}</InlineError>
                ) : qtd !== null ? (
                  <Hint>Vai pedir ×{formatarQuantidade(qtd)}</Hint>
                ) : null}
              </Field>
              <Linha2>
                <Field>
                  <Label htmlFor={`rqc-marca-${it.uid}`}>Marca preferencial (opcional)</Label>
                  <Input id={`rqc-marca-${it.uid}`} maxLength={120} value={it.marca} onChange={(e) => mudarItem(it.uid, "marca", e.target.value)} />
                </Field>
                <Field>
                  <Label htmlFor={`rqc-modelo-${it.uid}`}>Modelo / referência (opcional)</Label>
                  <Input id={`rqc-modelo-${it.uid}`} maxLength={120} value={it.modelo} onChange={(e) => mudarItem(it.uid, "modelo", e.target.value)} />
                </Field>
              </Linha2>
              <Field>
                <Label htmlFor={`rqc-esp-${it.uid}`}>Especificação (opcional)</Label>
                <TextArea id={`rqc-esp-${it.uid}`} placeholder="Ex.: mouse sem fio, USB, compatível com Windows." value={it.especificacao} onChange={(e) => mudarItem(it.uid, "especificacao", e.target.value)} />
              </Field>
              <Field>
                <Label htmlFor={`rqc-obs-${it.uid}`}>Observação (opcional)</Label>
                <Input id={`rqc-obs-${it.uid}`} maxLength={300} value={it.observacao} onChange={(e) => mudarItem(it.uid, "observacao", e.target.value)} />
              </Field>
            </Card>
          );
        })}

        <AddBtn type="button" onClick={() => setItens((l) => [...l, novoItem()])}>
          <FiPlus size={16} /> Adicionar item
        </AddBtn>

        <Card>
          <Label as="div">Anexos (até {maxAnexos}) — foto, print, orçamento, PDF</Label>
          {anexos.map((a, i) => (
            <div key={`${a.name}-${i}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, minWidth: 0 }}>
              <FiPaperclip size={13} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.name}>{a.name}</span>
              <button type="button" className="btn btn--secondary btn--sm" aria-label={`Remover ${a.name}`} onClick={() => setAnexos((l) => l.filter((_, j) => j !== i))}>
                <FiX size={12} />
              </button>
            </div>
          ))}
          {anexos.length < maxAnexos ? (
            <input
              type="file"
              multiple
              accept={EXTENSOES_ACEITAS}
              aria-label="Anexar arquivos"
              onChange={(e) => { adicionarAnexos(e.target.files); e.target.value = ""; }}
            />
          ) : null}
        </Card>

        {erroServidor ? <ErroBox role="alert">{erroServidor}</ErroBox> : null}
        {tentouEnviar && Object.keys(erros).length ? <ErroBox role="alert">Verifique os campos destacados.</ErroBox> : null}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <BtnSecundario type="button" onClick={() => navigate("..")}>Cancelar</BtnSecundario>
          <AsyncButton variant="primary" onClick={enviar} silentError silentSuccess>
            Enviar ao Compras
          </AsyncButton>
        </div>
      </div>
    </PageContainer>
  );
}
